import React from 'react';
import {
  Box,
  IconButton,
  Icons,
  Modal,
  Text,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { HOME_VIEW_ROLE_LABELS, HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';

const MODAL_TITLE_ID = 'upcoming-assignments-modal-title';

const STRINGS = {
  title: 'Upcoming assignments',
  empty: 'No upcoming assignments',
};

type UpcomingAssignmentsModalProps = {
  open: boolean;
  onClose: () => void;
  assignments: HomeUpcomingAssignment[];
  viewRole: HomeViewRole;
  getTermLabel?: (spaceId: string) => string | null;
  onOpenAssignment?: (
    assignment: HomeUpcomingAssignment
  ) => void | Promise<void>;
};

const UpcomingAssignmentsModal = ({
  open,
  onClose,
  assignments,
  viewRole,
  getTermLabel,
  onOpenAssignment,
}: UpcomingAssignmentsModalProps) => {
  const { palette } = useTheme();

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={MODAL_TITLE_ID}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={MODAL_TITLE_ID}
        sx={{
          width: { xs: 'calc(100vw - 32px)', sm: 616 },
          maxHeight: { xs: 'calc(100vh - 64px)', sm: '80vh' },
          bgcolor: palette.background.paper,
          borderRadius: '12px',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={{ xs: 2, sm: 3 }}
          pt={3}
          pb={2}
        >
          <Text
            id={MODAL_TITLE_ID}
            component="h2"
            tabIndex={0}
            sx={{
              fontWeight: 600,
              fontSize: '1.25rem',
              color: palette.primary.main,
            }}
          >
            {STRINGS.title}
          </Text>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close upcoming assignments dialog"
            sx={{ color: palette.text.secondary }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        <Box px={{ xs: 2, sm: 3 }} pb={3} sx={{ overflowY: 'auto', flex: 1 }}>
          {assignments.length ? (
            assignments.map((assignment, index) => (
              <UpcomingAssignmentRow
                key={assignment.id}
                assignment={assignment}
                viewRole={viewRole}
                termLabel={getTermLabel?.(assignment.spaceId) ?? null}
                showDivider={index < assignments.length - 1}
                onOpen={onOpenAssignment}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" py={2}>
              {STRINGS.empty} in {HOME_VIEW_ROLE_LABELS[viewRole].toLowerCase()}{' '}
              view.
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default UpcomingAssignmentsModal;
